import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Chip,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Grid,
  MenuItem
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Computer as ComputerIcon,
  Apps as AppsIcon,
  Memory as MemoryIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';

const SoftwareDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [software, setSoftware] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [editingVersion, setEditingVersion] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    type: 'operating_system',
    vendor: '',
    description: '',
    official_website: ''
  });

  const [versionFormData, setVersionFormData] = useState({
    version: '',
    release_date: '',
    end_of_support: '',
    is_current: false,
    notes: ''
  });

  useEffect(() => {
    fetchSoftware();
  }, [id]);

  const fetchSoftware = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/software/catalog/${id}`);
      setSoftware(response.data);
      setFormData({
        name: response.data.name,
        type: response.data.type,
        vendor: response.data.vendor || '',
        description: response.data.description || '',
        official_website: response.data.official_website || ''
      });
    } catch (err) {
      setError('Ошибка загрузки данных ПО');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`/software/catalog/${id}`, formData);
      setEditDialogOpen(false);
      fetchSoftware();
    } catch (err) {
      setError('Ошибка сохранения ПО');
      console.error(err);
    }
  };

  const handleOpenVersionDialog = (version = null) => {
    if (version) {
      setEditingVersion(version);
      setVersionFormData({
        version: version.version,
        release_date: version.release_date ? version.release_date.split('T')[0] : '',
        end_of_support: version.end_of_support ? version.end_of_support.split('T')[0] : '',
        is_current: version.is_current,
        notes: version.notes || ''
      });
    } else {
      setEditingVersion(null);
      setVersionFormData({
        version: '',
        release_date: '',
        end_of_support: '',
        is_current: false,
        notes: ''
      });
    }
    setVersionDialogOpen(true);
  };

  const handleSaveVersion = async () => {
    try {
      const cleanedData = {
        version: versionFormData.version,
        release_date: versionFormData.release_date || null,
        end_of_support: versionFormData.end_of_support || null,
        is_current: versionFormData.is_current || false,
        notes: versionFormData.notes || null
      };

      if (editingVersion) {
        await axios.put(`/software/versions/${editingVersion.id}`, cleanedData);
      } else {
        await axios.post(`/software/catalog/${id}/versions`, cleanedData);
      }

      setVersionDialogOpen(false);
      setEditingVersion(null);
      fetchSoftware();
    } catch (err) {
      setError(editingVersion ? 'Ошибка обновления версии' : 'Ошибка добавления версии');
      console.error(err);
    }
  };

  const handleDeleteVersion = async (versionId) => {
    if (!window.confirm('Удалить эту версию?')) return;

    try {
      await axios.delete(`/software/versions/${versionId}`);
      fetchSoftware();
    } catch (err) {
      setError('Ошибка удаления версии');
      console.error(err);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'operating_system':
        return <ComputerIcon fontSize="large" />;
      case 'application':
        return <AppsIcon fontSize="large" />;
      case 'firmware':
        return <MemoryIcon fontSize="large" />;
      default:
        return <ComputerIcon fontSize="large" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'operating_system':
        return 'Операционная система';
      case 'application':
        return 'Приложение';
      case 'firmware':
        return 'Прошивка';
      default:
        return type;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  const canManage = user?.is_superuser || user?.permissions?.includes('software.manage');
  const canView = user?.is_superuser || user?.permissions?.includes('software.view');

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">У вас нет прав для просмотра каталога ПО</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Загрузка...</Typography>
      </Box>
    );
  }

  if (!software) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">ПО не найдено</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/software-catalog')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1 }}>
          {getTypeIcon(software.type)}
          <Box>
            <Typography variant="h4">{software.name}</Typography>
            <Typography variant="body1" color="text.secondary">
              {software.vendor || 'Без производителя'}
            </Typography>
          </Box>
        </Box>
        {canManage && (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => setEditDialogOpen(true)}
          >
            Редактировать
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Tabs value={currentTab} onChange={(e, value) => setCurrentTab(value)} sx={{ mb: 3 }}>
        <Tab label="Информация" />
        <Tab label={`Версии (${software.versions?.length || 0})`} />
      </Tabs>

      {currentTab === 0 && (
        <Card>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Тип</Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={getTypeLabel(software.type)}
                    color="primary"
                    icon={getTypeIcon(software.type)}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Производитель</Typography>
                <Typography variant="body1">{software.vendor || '-'}</Typography>
              </Grid>

              {software.official_website && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Официальный сайт</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button
                      startIcon={<LinkIcon />}
                      href={software.official_website}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {software.official_website}
                    </Button>
                  </Box>
                </Grid>
              )}

              {software.description && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Описание</Typography>
                  <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                    {software.description}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Chip
                    label={`${software.versions?.length || 0} версий`}
                    color="secondary"
                  />
                  <Chip
                    label={`Используется на ${software.device_count || 0} устройствах`}
                    color="info"
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {currentTab === 1 && (
        <Box>
          {canManage && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenVersionDialog()}
              >
                Добавить версию
              </Button>
            </Box>
          )}

          {software.versions && software.versions.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Версия</TableCell>
                    <TableCell>Дата выпуска</TableCell>
                    <TableCell>Окончание поддержки</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Заметки</TableCell>
                    {canManage && <TableCell align="right">Действия</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {software.versions.map((version) => (
                    <TableRow key={version.id}>
                      <TableCell>
                        <Typography variant="body1" fontWeight={version.is_current ? 'bold' : 'normal'}>
                          {version.version}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(version.release_date)}</TableCell>
                      <TableCell>{formatDate(version.end_of_support)}</TableCell>
                      <TableCell>
                        {version.is_current && (
                          <Chip label="Текущая" size="small" color="success" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {version.notes || '-'}
                        </Typography>
                      </TableCell>
                      {canManage && (
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenVersionDialog(version)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteVersion(version.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">Нет добавленных версий</Alert>
          )}
        </Box>
      )}

      {/* Edit Software Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Редактировать ПО</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Название"
              fullWidth
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            <TextField
              label="Тип"
              fullWidth
              required
              select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            >
              <MenuItem value="operating_system">Операционная система</MenuItem>
              <MenuItem value="application">Приложение</MenuItem>
              <MenuItem value="firmware">Прошивка</MenuItem>
            </TextField>
            <TextField
              label="Производитель"
              fullWidth
              value={formData.vendor}
              onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
            />
            <TextField
              label="Описание"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <TextField
              label="Официальный сайт"
              fullWidth
              value={formData.official_website}
              onChange={(e) => setFormData({ ...formData, official_website: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleSave} variant="contained">
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Version Dialog */}
      <Dialog open={versionDialogOpen} onClose={() => setVersionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingVersion ? 'Редактировать версию' : 'Добавить версию'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Версия"
              fullWidth
              required
              value={versionFormData.version}
              onChange={(e) => setVersionFormData({ ...versionFormData, version: e.target.value })}
            />
            <TextField
              label="Дата выпуска"
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
              value={versionFormData.release_date}
              onChange={(e) => setVersionFormData({ ...versionFormData, release_date: e.target.value })}
            />
            <TextField
              label="Окончание поддержки"
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
              value={versionFormData.end_of_support}
              onChange={(e) => setVersionFormData({ ...versionFormData, end_of_support: e.target.value })}
            />
            <TextField
              label="Заметки"
              fullWidth
              multiline
              rows={2}
              value={versionFormData.notes}
              onChange={(e) => setVersionFormData({ ...versionFormData, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleSaveVersion} variant="contained">
            {editingVersion ? 'Сохранить' : 'Добавить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SoftwareDetail;
